import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { onSessionExpired, clearSessionToken, storeSessionToken, sessionSnapshot, isCurrentSession, InvalidSessionError } from "@/lib/session-expiration";
import { clearAuthenticatedCache } from "@/lib/query-client";

import {
  getCurrentPlatformUser,
  loginPlatformUser,
  type PlatformLoginRequest,
  type PlatformLoginResponse,
  type PlatformUser,
} from "../api/platformAuthApi";

const PLATFORM_TOKEN_STORAGE_KEY = "platform_access_token";

type PlatformAuthContextValue = {
  user: PlatformUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: PlatformLoginRequest) => Promise<void>;
  logout: () => void;
};

// Context is exported alongside its provider to preserve the existing consumer API.
// eslint-disable-next-line react-refresh/only-export-components
export const PlatformAuthContext = createContext<
  PlatformAuthContextValue | undefined
>(undefined);

type PlatformAuthProviderProps = {
  children: ReactNode;
};

export function PlatformAuthProvider({
  children,
}: PlatformAuthProviderProps) {
  const [user, setUser] = useState<PlatformUser | null>(null);

  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(PLATFORM_TOKEN_STORAGE_KEY),
  );

  const [isLoading, setIsLoading] = useState(true);

  const clearAuthentication = useCallback(() => {
    clearSessionToken("platform");
    clearAuthenticatedCache();
    setToken(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  useEffect(() => onSessionExpired((scope) => {
    if (scope === "platform") {
      setToken(null);
      setUser(null);
      setIsLoading(false);
    }
  }), []);

  const storeAuthentication = useCallback(
    (result: PlatformLoginResponse) => {
      clearAuthenticatedCache();
      storeSessionToken("platform", result.access_token);

      setToken(result.access_token);
      setUser(result.user);
    },
    [],
  );

  useEffect(() => {
    let isMounted = true;
    const snapshot = sessionSnapshot("platform");

    async function restoreSession() {
      if (!token) {
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }

        return;
      }

      try {
        const currentUser = await getCurrentPlatformUser();

        if (isMounted && isCurrentSession("platform", snapshot)) {
          setUser(currentUser);
        }
      } catch {
        // Invalid sessions are handled centrally. Other failures preserve credentials.
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, [token]);

  async function login(data: PlatformLoginRequest) {
    const snapshot = sessionSnapshot("platform");
    const response = await loginPlatformUser(data);
    if (!isCurrentSession("platform", snapshot)) throw new InvalidSessionError();
    storeAuthentication(response);
  }

  function logout() {
    clearAuthentication();
  }

  const value = {
    user,
    token,
    isAuthenticated: Boolean(user && token),
    isLoading,
    login,
    logout,
  };

  return (
    <PlatformAuthContext.Provider value={value}>
      {token && !user && !isLoading ? (
        <div role="alert" className="p-6">
          <p>Unable to access your account. Please try again or contact your administrator.</p>
          <button onClick={() => window.location.reload()}>Try again</button>
        </div>
      ) : children}
    </PlatformAuthContext.Provider>
  );
}
